// src/components/media360/domain/groupExperiences.js
// Agrupa el listado plano de GCS en “experiencias” (media principal + audio opcional).

import { parseObjectName } from "./naming";

/**
 * @typedef {Object} GcsItem
 * @property {string} name
 * @property {string} publicUrl
 * @property {number=} size
 * @property {string|number|Date=} updated
 */

/**
 * @typedef {Object} Experience
 * @property {string} groupKey
 * @property {"video"|"image"} mediaKind
 * @property {"mono"|"3d-tb"|"3d-sbs"} mode
 * @property {boolean} loop
 * @property {GcsItem} media
 * @property {GcsItem | null} audio
 */

/**
 * Convierte items sueltos (GCS) en experiencias agrupadas por convención de nombre.
 * - La experiencia “vive” si hay media principal (video o image).
 * - El audio se adjunta si existe un objeto de audio con sufijo __audio.
 */
export function groupItemsToExperiences(items = []) {
  /** @type {Map<string, any>} */
  const map = new Map();

  for (const it of items || []) {
    const name = it?.name || "";
    const meta = parseObjectName(name);
    if (meta.kind === "unknown") continue;

    const key = meta.groupKey;
    if (!map.has(key)) {
      map.set(key, {
        groupKey: key,
        mediaKind: null,
        mode: meta.mode,
        loop: meta.loop,
        media: null,
        audio: null,
        // Para ordenar, usamos updated si está
        _sortUpdated: it?.updated ? new Date(it.updated).getTime() : 0,
      });
    }

    const acc = map.get(key);

    // Propagamos mode/loop si el nombre lo contiene (preferimos info de media)
    acc.mode = acc.mode || meta.mode;
    acc.loop = acc.loop || meta.loop;
    acc._sortUpdated = Math.max(
      acc._sortUpdated || 0,
      it?.updated ? new Date(it.updated).getTime() : 0,
    );

    if (meta.kind === "audio") {
      // Solo consideramos “audio asociado” si termina en __audio
      if (meta.audioAttachment) acc.audio = it;
      continue;
    }

    if (meta.kind === "video" || meta.kind === "image") {
      // Si hay conflicto (video+image), preferimos video como media principal
      if (!acc.media) {
        acc.media = it;
        acc.mediaKind = meta.kind;
        acc.mode = meta.mode;
        acc.loop = meta.loop;
      } else if (acc.mediaKind === "image" && meta.kind === "video") {
        acc.media = it;
        acc.mediaKind = "video";
        acc.mode = meta.mode;
        acc.loop = meta.loop;
      }
    }
  }

  /** @type {Experience[]} */
  const experiences = [];
  for (const acc of map.values()) {
    if (!acc.media || !acc.mediaKind) continue;
    experiences.push({
      groupKey: acc.groupKey,
      mediaKind: acc.mediaKind,
      mode: acc.mode || "mono",
      loop: Boolean(acc.loop),
      media: acc.media,
      audio: acc.audio || null,
    });
  }

  // Orden: más reciente primero (si hay updated), si no por nombre
  experiences.sort((a, b) => {
    const au = a?.media?.updated ? new Date(a.media.updated).getTime() : 0;
    const bu = b?.media?.updated ? new Date(b.media.updated).getTime() : 0;
    if (bu !== au) return bu - au;
    return String(a.groupKey).localeCompare(String(b.groupKey));
  });

  return experiences;
}
